import { IntegrationType } from '@/shared/types/integration';
import { WebhookPayload, WebhookType } from '@/shared/types/webhook';

// URL helper functions
/** Query before fragment: appended last, it becomes part of the fragment id and matches no heading. */
export const getDocsUrl = (url = '') => {
  const hashIndex = url.indexOf('#');
  const path = (hashIndex === -1 ? url : url.slice(0, hashIndex)).replace(/^\//, '');
  const hash = hashIndex === -1 ? '' : url.slice(hashIndex);
  return `https://docs.moveo.ai/${path}?utm_source=console${hash}`;
};
export const getLandingPageUrl = (url = '') => `https://moveo.ai${url}?utm_source=console`;

// Documentation URLs (docs.moveo.ai)
export const AI_AGENTS_DOCS_URL = getDocsUrl('/docs/ai-agents/overview');
export const GUIDELINES_DOCS_URL = getDocsUrl('/docs/ai-agents/ai-agents-knowledge#guidelines');
export const LOCAL_TOOLS_DOCS_URL = getDocsUrl('/docs/ai-agents/local-tools');
export const MCP_DOCS_URL = getDocsUrl('/docs/ai-agents/mcp-servers');
export const VERSIONS_DOCS_URL = getDocsUrl('/docs/ai-agents/versions');
export const COLLECTIONS_DOCS_URL = getDocsUrl('/docs/knowledge-base/knowledge-base-overview');
export const COLLECTIONS_WEBPAGE_SOURCE_DOCS_URL = getDocsUrl(
  '/docs/knowledge-base/knowledge-base-documents#webpage-source'
);
export const SUNCO_DOCS_URL = getDocsUrl('/docs/integrations/sunco#disconnection-procedure');
export const INTENTS_DOCS_URL = getDocsUrl('/docs/ai-agents/intents');
export const INTENT_TRIGGER_DOCS_URL = getDocsUrl(
  '/docs/ai-agents/triggers/trigger-text#how-to-use-an-intent-trigger'
);
export const LANGUAGE_MODELS_DOCS_URL = getDocsUrl('/docs/platform/platform-language-models');
export const CONDITION_DOCS_URL = getDocsUrl('/docs/ai-agents/operations/conditions');
export const ENVIRONMENTS_DOCS_URL = getDocsUrl('/docs/environments/environments-overview');
export const CAMPAIGNS_DOCS_URL = getDocsUrl('/docs/campaigns');
export const EVENT_RESPONSE_DOCS_URL = getDocsUrl('/docs/ai-agents/triggers/triggers-overview');
export const EVENT_RESOLVE_DOCS_URL = getDocsUrl('/docs/ai-agents/responses/responses-overview');
export const EVENT_UNKNOWN_DOCS_URL = getDocsUrl('/docs/ai-agents/triggers/unknown');
export const GOOGLE_SHEET_DOCS_URL = getDocsUrl('/docs/ai-agents/responses/googlesheet');
export const JUMP_TO_DOCS_URL = getDocsUrl('/docs/ai-agents/operations/operations-event');
export const PAUSE_DOCS_URL = getDocsUrl('/docs/ai-agents/operations/pause');
export const QUESTION_DOCS_URL = getDocsUrl('/docs/ai-agents/operations/questions');
export const REPLAY_RESPONSE_DOCS_URL = getDocsUrl('/docs/ai-agents/operations/replay');
export const SWITCH_CHANNEL_DOCS_URL = getDocsUrl('/docs/ai-agents/operations/switch-channel');
export const RULES_DOCS_URL = getDocsUrl('/docs/environments/environments-rules');
export const SET_VARIABLES_DOCS_URL = getDocsUrl('/docs/ai-agents/operations/set-variables');
export const INACTIVITY_TIMEOUT_DOCS_URL = getDocsUrl(
  '/docs/ai-agents/advanced#inactivity-timeout'
);
export const CHAT_URL = getDocsUrl('');
export const SUPPORT_URL = getDocsUrl('');
export const DOCS_API_URL = getDocsUrl('/docs/api/api-overview');
export const EXCLUDED_URLS_URL = getDocsUrl(
  '/docs/knowledge-base/knowledge-base-website#excluded-urls'
);
export const SEED_URLS_URL = getDocsUrl('/docs/knowledge-base/knowledge-base-website#full-website');
export const API_KEYS_DOCS_URL = getDocsUrl('/docs/platform/platform-api-keys');
export const WEBHOOKS_DOCS_URL = getDocsUrl('/docs/ai-agents/ai-agents-webhooks');
export const ANALYTICS_COVERAGE_DOCS_URL = getDocsUrl(
  '/docs/analytics/ai-agent-effectiveness#coverage'
);
export const ANALYTICS_CONTAINMENT_DOCS_URL = getDocsUrl(
  '/docs/analytics/ai-agent-effectiveness#containment'
);
export const EVENT_NOTIFICATIONS_DOCS_URL = getDocsUrl(
  '/docs/platform/plugins/create-event-notification'
);
export const EVENT_NOTIFICATION_URL_DOCS_URL = getDocsUrl(
  '/docs/platform/plugins/create-event-notification#3-url'
);
export const EVENT_NOTIFICATION_SECRET_DOCS_URL = getDocsUrl(
  '/docs/platform/plugins/create-event-notification#4-secret'
);

export const DEFAULT_START_MESSAGE = 'defaultstartmessage';

// Moveo.ai URLs
export const STATUS_URL = 'https://status.moveo.ai?utm_source=console';
export const PRICING_URL = 'https://moveo.ai/pricing?utm_source=console&utm_medium=billing';
export const PRIVACY_POLICY_URL = 'https://moveo.ai/privacy-policy/?utm_source=console';
export const TERMS_OF_USE_URL = 'https://moveo.ai/terms-of-use/?utm_source=console';
export const SUBSCRIPTION_AGREEMENT_URL =
  'https://moveo.ai/subscription-agreement/?utm_source=console';
export const API_HOST = 'https://api.moveo.ai?utm_source=console';
export const WEB_CLIENT_URL = 'https://web.moveo.ai';

// External service URLs
export const CONTACT_SALES_URL =
  'https://meetings-eu1.hubspot.com/konstantinos-perdios?uuid=d6151bc3-af79-4e0b-9f60-fd9f65&utm_source=console';
export const ZENDESK_DOCS_URL = 'https://support.zendesk.com';
export const INTERCOM_ENDPOINT = 'https://api.intercom.io';

export const SIGNUP_TRIAL_PERIOD_IN_DAYS = parseInt(window.SIGNUP_TRIAL_PERIOD_IN_DAYS || '30');
export const API_URL = process.env.NODE_ENV === 'test' ? 'https://localhost:5000' : '';

export const STRIPE_PUBLIC_KEY = window.STRIPE_PUBLIC_KEY;

export const CHANNELS_URL: string = window.CHANNELS_URL || 'https://channels.dev.moveo.ai';

/**
 * The URL for the WebSocket Channels API.
 * It is constructed by replacing 'channels' with 'channels-ws' in the CHANNELS_URL.
 */
export const CHANNELS_WS_URL: string = CHANNELS_URL.replace('channels', 'channels-ws');

export const API_KEY_MASK = '********';
export const isApiKeyMasked = (key: string | null | undefined): boolean =>
  typeof key === 'string' && key.includes(API_KEY_MASK);

export const IMAGE_TAG: string = window.IMAGE_TAG || 'local';

export const EXTERNAL_COS_BUCKET: string = window.EXTERNAL_COS_BUCKET || 'media.dev.moveo.ai';

export const WHATSAPP_BRAIN_TEST_URL: string =
  window.WHATSAPP_BRAIN_TEST_URL || `https://wa.me/15557010447?text=${DEFAULT_START_MESSAGE}`;

export const WHATSAPP_OAUTH_CONFIG_ID = window.WHATSAPP_OAUTH_CONFIG_ID || '1841659707274547';

export const ISO = 'ISO 27001:2013';
export const ISO_BLOG_POST = 'https://moveo.ai/blog/iso-27001-certification/';

let channels = [];
try {
  channels = JSON.parse(window.CHANNELS_SUPPORTED || '[]');
} catch {
  console.debug('WARN: Cannot parse CHANNELS_SUPPORTED');
}
export const CHANNELS_SUPPORTED: string[] = channels;

let collectionLanguages = [];
try {
  collectionLanguages = JSON.parse(window.COLLECTION_LANGUAGES || '[]');
} catch {
  console.debug('WARN: Cannot parse COLLECTION_LANGUAGES');
}
export const COLLECTION_LANGUAGES: string[] = collectionLanguages;

let uiLanguages = [];
try {
  uiLanguages = JSON.parse(window.UI_LANGUAGES || '[]');
} catch {
  console.debug('WARN: Cannot parse CHANNELS_SUPPORTED');
}

export const UI_LANGUAGES: string[] = uiLanguages;

export let enhancerLanguages: string[] = [];
try {
  enhancerLanguages = JSON.parse(window.ENHANCER_LANGUAGES || '[]');
} catch {
  console.debug('WARN: Cannot parse ENHANCER_LANGUAGES');
}

export const ENHANCER_LANGUAGES: string[] = enhancerLanguages;

export let enhancerAccountIds: string[] = [];
try {
  enhancerAccountIds = JSON.parse(window.ENHANCER_ACCOUNT_IDS || '[]');
} catch {
  console.debug('WARN: Cannot parse ENHANCER_ACCOUNT_IDS');
}

export const ENHANCER_ACCOUNT_IDS: string[] = enhancerAccountIds;

export const ACCOUNT_ID_COOKIE_KEY = 'accountId';

// The following variables are hardcoded to Production and used by web-client
// and brain templates
export const MOVEO_INTEGRATION_ID = '540e38c4-8064-40e2-9a00-cf26e7560c4a';
export const MOVEO_CHANNELS_WS_URL = 'https://channels-ws.moveo.ai';

// UI constants
export const sidebarWidth = 200;
export const subNavWidth = 240;
export const toolkitWidth = 240;

export const roles = [
  { value: 'owner', label_key: '', disabled: true },
  { value: 'admin', label_key: 'invitations.roles.adminDesc' },
  { value: 'writer', label_key: 'invitations.roles.writerDesc' },
  { value: 'reader', label_key: 'invitations.roles.readerDesc' },
];

export const ADMIN = 'facade00-0000-4000-a000-000000000003';
export const CHAT_MANAGER = 'facade00-0000-4000-a000-000000000006';
export const CHAT_AGENT = 'facade00-0000-4000-a000-000000000005';
export const BUILDER = 'facade00-0000-4000-a000-000000000004';
export const READER = 'facade00-0000-4000-a000-000000000001';
export const WRITER = 'facade00-0000-4000-a000-000000000002';
export const BILLING = 'facade00-0000-4000-a000-000000000008';

export const MAX_UPLOAD_SIZE = 10000000;
export const MAX_MEDIA_UPLOAD_SIZE = 5000000;

// Mock UUIDs for testing - all valid UUIDv4s with obvious repeating patterns
export const ACCOUNT_ID_MOCK = '00000000-0000-4000-8000-000000000000';
export const USER_ID_MOCK = '11111111-1111-4111-8111-111111111111';
export const USER_ID_MOCK_2 = '11111111-1111-4111-8111-111111111112';
export const BRAIN_ID_MOCK = '22222222-2222-4222-8222-222222222222';
export const DESK_ID_MOCK = '33333333-3333-4333-8333-333333333333';
export const DIALOG_ID_MOCK = '44444444-4444-4444-8444-444444444444';
export const WEBHOOK_ID_MOCK = '55555555-5555-4555-8555-555555555555';
export const RULE_ID_MOCK = '66666666-6666-4666-8666-666666666666';
export const SESSION_ID_MOCK = '77777777-7777-4777-8777-777777777777';
export const INTEGRATION_ID_MOCK = '88888888-8888-4888-8888-888888888888';
export const MESSAGE_ID_MOCK = '99999999-9999-4999-8999-999999999999';
export const MACRO_ID_MOCK = 'aaaaaaaa-aaaa-4aaa-8aaa-aaaaaaaaaaaa';
export const COLLECTION_ID_MOCK = 'bbbbbbbb-bbbb-4bbb-8bbb-bbbbbbbbbbbb';
export const DATASOURCE_ID_MOCK = 'cccccccc-cccc-4ccc-8ccc-cccccccccccc';
export const BROADCAST_ID_MOCK = 'dddddddd-dddd-4ddd-8ddd-dddddddddddd';
export const VIEW_ID_MOCK = 'eeeeeeee-eeee-4eee-8eee-eeeeeeeeeeee';
export const ACCOUNT_SLUG_MOCK = 'team-disney';

export const getZendeskAuthUrl = (s: string) =>
  `https://${s}.zendesk.com/oauth2/chat/authorizations/new`;

export const FACEBOOK_API_VERSION = window.FACEBOOK_API_VERSION || 'v26.0';
export const FACEBOOK_AUTHORIZATION_URL = `https://www.facebook.com/${FACEBOOK_API_VERSION}/dialog/oauth`;

export const SUNCO_AUTHORIZATION_URL = 'https://api.smooch.io/oauth/authorize';

export const FACEBOOK_SCOPES = [
  'public_profile',
  'email',
  'pages_messaging',
  'business_management',
  'pages_manage_metadata',
  'pages_read_engagement',
  'pages_show_list',
];

export const INSTAGRAM_SCOPES = [
  'public_profile',
  'email',
  'pages_messaging',
  'business_management',
  'pages_manage_metadata',
  'pages_read_engagement',
  'instagram_basic',
  'instagram_manage_messages',
  'pages_show_list',
];

export const INTERCOM_AUTHORIZATION_URL = 'https://app.intercom.com/oauth';

export const LIVECHAT_AUTHORIZATION_URL = 'https://accounts.livechat.com/';

export const LIVECHAT_DEVELOPER_CONSOLE_URL = 'https://platform.text.com/console/apps/your';

export const LIVECHAT_APPS_GUIDE_URL = 'https://platform.text.com/docs/guides/livechat-apps';

export const FRONT_AUTHORIZATION_URL = 'https://app.frontapp.com/oauth/authorize';

export const EMAIL_INBOUND_DOMAIN = window.EMAIL_INBOUND_DOMAIN;

export const SYSTEM_INTENTS = ['offtopics'];

export const PAGINATION_LIMIT = 500;
export const INCREASED_PAGINATION_LIMIT = 500;
export const INFINITE_SCROLLING_LIMIT = 25;
export const TABLE_PAGINATION = 20;
export const IMPROVEMENTS_PAGINATION_LIMIT = 100;

// Column presets with TanStack-native numeric sizing (size, minSize, maxSize)
export const COLUMN_PRESETS = {
  SM: { size: 48, minSize: 48, maxSize: 96 },
  MD: { size: 180, minSize: 96, maxSize: 292 },
  LG: { size: 292, minSize: 292, maxSize: 384 },
  FLEX: { minSize: 384, maxSize: 768 },
} as const;

// Standard table-page container: centered max-width box that fills its bounded parent so the
// table scrolls internally (header/toolbar pinned, pagination anchored). Pair with `<DataTable.Root fill>`.
export const TABLE_LAYOUT_FILL_CLASSES =
  'mx-auto w-full max-w-(--table-layout-max-width) px-4 pt-4 pb-6 lg:px-5 xl:px-6 2xl:px-8 flex h-full min-h-0 flex-col';

// Same as TABLE_LAYOUT_FILL_CLASSES but spans the full available width (no max-width cap).
export const TABLE_LAYOUT_FILL_FULL_WIDTH_CLASSES =
  'flex h-full min-h-0 w-full flex-col px-4 pt-4 pb-6 lg:px-5 xl:px-6 2xl:px-8';

export const ROW_HIGHLIGHT_BASE =
  'shadow-[inset_0_0_0_0px_var(--border)] transition-[box-shadow,background-color]';

export const ACTIVE_ROW_HIGHLIGHT =
  'rounded-md bg-background shadow-[inset_0_0_0_1px_var(--border)] [&>td:first-child]:rounded-l-md [&>td:last-child]:rounded-r-md';

export const X_MOVEO_ACCOUNT_ID = 'X-Moveo-Account-id';
export const X_MOVEO_ACCOUNT_SLUG = 'X-Moveo-Account-Slug';

const baseTestContext: NonNullable<WebhookPayload['context']> = {
  user: {
    display_name: 'John Doe',
    email: 'john@example.com',
    user_id: 'V1StGXR8_Z5jdHi6B',
    external_id: 'IRFa-VaY2b',
    language: 'en',
  },
  total_months: 3,
  destination: 'England',
  'sys-unknown_counter': 0,
  'sys-user_message_counter': 1,
  'sys-channel': IntegrationType.WEB,
  total_expenses: 500,
  tags: ['premium', 'vip'],
};

/** Default payload for ACTION webhooks */
const actionTestPayload: WebhookPayload = {
  channel: IntegrationType.WEB,
  lang: 'en',
  context: baseTestContext,
  input: { text: 'Hello, my name is George and I would like to go for erasmus' },
  intents: [{ intent: 'erasmus', confidence: 0.89 }],
  entities: [
    { entity: 'firstname', value: 'George', start: 18, end: 36, confidence: 0.95, type: 'pattern' },
  ],
  user_message_counter: 1,
};

/** Default payload for FIRST_MESSAGE and PRE_MESSAGE webhooks */
const preMessageTestPayload: WebhookPayload = {
  channel: IntegrationType.WEB,
  lang: 'en',
  context: baseTestContext,
  input: { text: 'Hello, I need help with my account' },
  business_closed: false,
};

/** Default payload for POST_MESSAGE webhooks (includes output) */
const postMessageTestPayload: WebhookPayload = {
  channel: IntegrationType.WEB,
  lang: 'en',
  context: baseTestContext,
  input: { text: 'Hello, my name is George and I would like to go for erasmus' },
  intents: [{ intent: 'erasmus', confidence: 0.89 }],
  entities: [
    { entity: 'firstname', value: 'George', start: 18, end: 36, confidence: 0.95, type: 'pattern' },
  ],
  user_message_counter: 1,
  output: [{ type: 'text', texts: ['How can I help you today?'] }],
};

const testWebhookPayloads: Partial<Record<WebhookType, WebhookPayload>> = {
  [WebhookType.ACTION]: actionTestPayload,
  [WebhookType.POST_MESSAGE]: postMessageTestPayload,
  [WebhookType.FIRST_MESSAGE]: preMessageTestPayload,
  [WebhookType.PRE_MESSAGE]: preMessageTestPayload,
  // LOG_MESSAGE shape is close enough to PRE_MESSAGE for a default test fixture
  [WebhookType.LOG_MESSAGE]: preMessageTestPayload,
};

export const getTestWebhookValues = (type: WebhookType): WebhookPayload =>
  testWebhookPayloads[type] ?? actionTestPayload;

/** The envelope every outbound webhook body carries, whatever its type. */
const webhookEnvelopeProperties = {
  channel: { type: 'string' },
  channel_type: { type: 'string' },
  session_id: { type: 'string' },
  desk_id: { type: 'string' },
  integration_id: { type: 'string' },
  brain_id: { type: 'string' },
  lang: { type: 'string' },
  context: {
    type: 'object',
    additionalProperties: true,
    properties: {
      user: {
        type: 'object',
        properties: {
          display_name: { type: 'string' },
          email: { type: 'string' },
          user_id: { type: 'string' },
          external_id: { type: 'string' },
          language: { type: 'string' },
        },
      },
      tags: { type: 'array', items: { $ref: 'tag.json' } },
    },
  },
  timestamp: { type: 'integer' },
};

const tagSchema = { uri: 'tag.json', schema: { type: 'string' } };

export const testWebhookSchemas = [
  {
    uri: 'webhook.json',
    fileMatch: ['*'],
    schema: {
      type: 'object',
      additionalProperties: false,
      properties: {
        ...webhookEnvelopeProperties,
        input: { type: 'object', properties: { text: { type: 'string' } }, required: ['text'] },
        intents: {
          type: 'array',
          items: {
            type: 'object',
            properties: { intent: { type: 'string' }, confidence: { type: 'number' } },
            required: ['intent', 'confidence'],
          },
        },
        entities: {
          type: 'array',
          items: {
            type: 'object',
            properties: {
              entity: { type: 'string' },
              value: { type: 'string' },
              start: { type: 'integer' },
              end: { type: 'integer' },
              confidence: { type: 'number' },
              type: { type: 'string' },
            },
            required: ['entity', 'value'],
          },
        },
        business_closed: { type: 'boolean' },
        user_message_counter: { type: 'integer' },
        debug: {
          type: 'object',
          properties: {
            dialog_stack: {
              type: 'array',
              items: {
                type: 'object',
                properties: { node_id: { type: 'string' }, name: { type: 'string' } },
              },
            },
          },
        },
        output: {
          type: 'array',
          items: {
            type: 'object',
            properties: {
              type: { type: 'string' },
              texts: { type: 'array', items: { type: 'string' } },
            },
          },
        },
      },
    },
  },
  tagSchema,
];

export const testWebhookPreNbaSchemas = [
  {
    uri: 'webhook-pre-nba.json',
    fileMatch: ['*'],
    schema: {
      type: 'object',
      properties: {
        subscriber_id: { type: 'string' },
        campaign_id: { type: 'string' },
        phone: { type: 'string' },
        email: { type: 'string' },
        context: { type: 'object', additionalProperties: true },
      },
      required: ['subscriber_id', 'campaign_id'],
      anyOf: [
        { required: ['email'], properties: { email: { minLength: 1 } } },
        { required: ['phone'], properties: { phone: { minLength: 1 } } },
      ],
      errorMessage: { anyOf: 'Provide at least one of "email" or "phone".' },
    },
  },
];

export const testWebhookAuthPreEnterSchemas = [
  {
    uri: 'webhook-auth-pre-enter.json',
    fileMatch: ['*'],
    schema: {
      type: 'object',
      additionalProperties: false,
      properties: { ...webhookEnvelopeProperties },
      required: ['session_id', 'context'],
    },
  },
  tagSchema,
];

export const testWebhookAuthValidationSchemas = [
  {
    uri: 'webhook-auth-validation.json',
    fileMatch: ['*'],
    schema: {
      type: 'object',
      additionalProperties: false,
      properties: { ...webhookEnvelopeProperties, value: {} },
      required: ['session_id', 'context', 'value'],
    },
  },
  tagSchema,
];

export const INTENTS_MOCK = [
  {
    intent: 'offtopics',
    brain_id: BRAIN_ID_MOCK,
    description: 'System intent',
    metadata: {},
    expressions: ['aaaa'],
    collection: '',
    created: '2020-01-02T00:00:00Z',
    updated: '2020-01-02T00:00:00',
  },
  {
    intent: 'test',
    brain_id: BRAIN_ID_MOCK,
    description: '',
    metadata: {},
    expressions: ['test', 'test'],
    collection: '',
    created: '2020-01-02T00:00:00Z',
    updated: '2020-01-02T00:00:00Z',
  },
];

export const ENTITIES_MOCK = [
  {
    entity: 'entity_name',
    brain_id: BRAIN_ID_MOCK,
    description: '',
    metadata: {},
    expressions: [],
    values: [{ type: 'synonym', value: 'mock1', synonyms: ['entity1'] }],
    fuzzy_match: true,
    collection: '',
    created: '2020-01-02T00:00:00Z',
    updated: '2020-01-02T00:00:00Z',
  },
  {
    entity: 'bbbbbb',
    brain_id: BRAIN_ID_MOCK,
    description: '',
    metadata: {},
    expressions: [],
    values: [{ type: 'synonym', value: 'mock2', synonyms: ['entity2'] }],
    fuzzy_match: true,
    collection: '',
    created: '2020-01-02T00:00:00Z',
    updated: '2020-01-02T00:00:00Z',
  },
];

export const MODERATE_EXECUTION_TIME = 5000;
export const BAD_EXECUTION_TIME = 8000;

export const MAX_UNCROPED_WEBHOOK_URL_CHARS = 55;
export const MAX_ENDING_URL_CHARS = 25;
export const THREE_DOTS_LENGTH = 3;

export const DEFAULT_COLLECTION = 'Default';

export const MIN_TRAINING_PHRASES = 5;

export const ACCEPTED_VIDEO_FORMATS = {
  'video/mp4': ['.mp4'],
  'video/webm': ['.webm'],
  'video/mpeg': ['.mpeg'],
};

export const ACCEPTED_IMAGE_FORMATS = {
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/gif': ['.gif'],
};

export const autoIntentLanguages = ['en', 'pt-br', 'es', 'el', 'fr', 'de'];

export const autoDialogLanguages = ['en', 'el'];

export const MAX_CONVERSATION_TAGS = 8;

export const MAX_EMAIL_RECIPIENTS = 8;

export const MAX_EXPANDED_CONDITIONS = 3;

export const DISPLAYED_TEXT_ACTION_OPTIONS = 2;

export const MAX_TEXT_ACTION_OPTIONS = 15;

export const LAST_ACTIONS = ['replay', 'close', 'event', 'reminder', 'sub_agent', 'question_set'];

export const FILES_BASE_URL = '/www/files';
export const FIFTEEN_MB = 15000000;
export const FIFTY_MB = 50000000;
export const HUNDRED_BYTES = 100;

export const supportEmail = 'support@moveo.ai';
export const supportBREmail = 'support-br@moveo.ai';

export const LOGIN_ROUTE = '/www/api/auth/login';
export const LOGOUT_ROUTE = '/www/api/auth/logout';

export const SCREEN_SIZES = { sm: 640, md: 768, lg: 1024, xl: 1280, '2xl': 1536 };

// Integration categories for organizing integrations in the UI
export const CONNECTIONS_CATEGORIES = {
  channels: [
    IntegrationType.WEB,
    IntegrationType.EMAIL,
    IntegrationType.WHATSAPP,
    IntegrationType.INFOBIP_SMS,
    IntegrationType.INFOBIP_RCS,
    IntegrationType.SMARTLEAD,
    IntegrationType.TWILIO_VOICE,
    IntegrationType.TELNYX_VOICE,
    IntegrationType.WEB_VOICE,
    IntegrationType.FACEBOOK,
    IntegrationType.INSTAGRAM,
    IntegrationType.VIBER,
    IntegrationType.MSTAT_VIBER,
    IntegrationType.SLACK,
    IntegrationType.CUSTOM,
  ],
  communicationPlatforms: [
    IntegrationType.SUNCO,
    IntegrationType.INTERCOM,
    IntegrationType.FRONT,
    IntegrationType.LIVECHAT,
    IntegrationType.ZENDESK,
  ],
} as const;
